import { isAxiosError, type AxiosRequestConfig } from 'axios'

import { handleException } from 'clients/exception-handling'
import type { ApiClient, TokenManager } from 'clients/yogi-api'
import type {
  ShareAllowlistResponse,
  SharedUsersResponse,
  ShareProjectWithUserPayload,
  ShareProjectWithUserResponse,
} from 'features/share/types'

const SHARE_ALLOWLIST_URL = '/client/settings/share-allowlist'

const toHubConfig = (proj_uuid: string): AxiosRequestConfig => ({
  headers: { 'X-Hub-UUID': proj_uuid },
})

export class ShareService {
  constructor(
    private client: ApiClient,
    private tokenManager: TokenManager,
  ) {}

  async fetchSharedUsers(proj_uuid: string): Promise<SharedUsersResponse> {
    const { data } = await this.client.get<SharedUsersResponse>(
      '/project/shared',
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  /** Extra email domains this hub allows cross-domain sharing with. */
  async fetchShareAllowlist(proj_uuid: string): Promise<string[]> {
    const { data: allowlist } = await this.client.get<ShareAllowlistResponse>(
      SHARE_ALLOWLIST_URL,
      toHubConfig(proj_uuid),
    )
    return allowlist.domains
  }

  /** Replaces the hub's allowlist; returns the saved (lowercased) domains. */
  async replaceShareAllowlist(
    proj_uuid: string,
    domains: string[],
  ): Promise<string[]> {
    const body: ShareAllowlistResponse = { domains }
    const { data: allowlist } = await this.client.put<ShareAllowlistResponse>(
      SHARE_ALLOWLIST_URL,
      body,
      toHubConfig(proj_uuid),
    )
    return allowlist.domains
  }

  async checkIfUserExists(email: string): Promise<boolean> {
    const { data } = await this.client.get<{ user_exists: boolean }>(
      '/user/exists',
      {
        params: { email },
      },
    )
    return data.user_exists
  }

  async shareProjectWithUncheckedUser(
    proj_uuid: string,
    shared_email: string,
    admin: boolean,
  ): Promise<boolean> {
    const userExists = await this.checkIfUserExists(shared_email)
    if (userExists) {
      await this.shareProjectWithUser(proj_uuid, {
        admin,
        shared_email,
        create_user: false,
      })
      return true
    }
    return false
  }

  async shareProjectWithUser(
    proj_uuid: string,
    payload: ShareProjectWithUserPayload,
  ): Promise<ShareProjectWithUserResponse> {
    const { data } = await this.client.post<ShareProjectWithUserResponse>(
      '/project/share',
      payload,
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  async unshareProjectWithUser(
    proj_uuid: string,
    shared_email: string,
  ): Promise<void> {
    const { data } = await this.client.delete<void>('/project/unshare', {
      params: { proj_uuid, shared_email },
    })

    return data
  }

  async fetchUrlForSharing(proj_uuid: string, on: boolean): Promise<string> {
    const { data } = await this.client.get<string>('/project/token', {
      params: { proj_uuid, on },
    })
    return data
  }

  async verifyAccess(
    proj_uuid: string,
    public_token: string,
  ): Promise<boolean> {
    try {
      const { data } = await this.client.get('/project/public', {
        params: { proj_uuid, public_token },
      })

      if (data) {
        this.tokenManager.setSharedToken(public_token)
        return true
      }
    } catch (cause) {
      if (
        !isAxiosError(cause) ||
        ![403, 404].includes(cause.response?.status ?? 0)
      )
        throw cause
      handleException(cause, {
        operation: 'project.shared-access.verify',
        disposition: 'expected',
        isExpectedError: (error) =>
          isAxiosError(error) &&
          [403, 404].includes(error.response?.status ?? 0),
        reason:
          'A missing or inaccessible public project is a denied-access result.',
      })
    }

    return false
  }
}
